/**
 * Layout Service - Web Worker Wrapper with Fallback
 *
 * Phase 3 Slice 9: Provides a unified interface for tree layout computation
 * that automatically uses a Web Worker when available, with graceful fallback
 * to main-thread computation if the worker fails or is unavailable.
 *
 * This service ensures:
 * 1. Large trees (500+ nodes) compute layout off the main thread (responsive UI)
 * 2. Fallback to main-thread computation if worker fails (no broken rendering)
 * 3. Identical layout results regardless of computation method
 * 4. Clean Promise-based API for easy integration with React
 *
 * Usage:
 * ```typescript
 * const layout = await computeLayoutAsync(persons, relationships, rootId, couples, options);
 * ```
 */

import type { PersonNodeCanvas, RelationshipEdge } from '@/types/tree-dashboard.types';
import type { Couple } from '@/types/entities';
import {
  computeTreeLayout,
  type TreeLayout,
} from '../utils/genealogyLayoutEngine';
import type { LayoutComputeMessage, LayoutResultMessage, LayoutErrorMessage } from '../workers/layoutWorker';

/**
 * Layout computation options
 */
export interface LayoutOptions {
  maxAncestorDepth?: number;
  maxDescendantDepth?: number;
}

/**
 * Singleton worker instance
 * Lazily initialized on first use
 */
let workerInstance: Worker | null = null;
let workerAvailable = true; // Tracks if worker initialization succeeded

/**
 * Initialize Web Worker for layout computation
 *
 * Creates a worker instance using Vite's worker import syntax.
 * If worker creation fails, marks worker as unavailable and logs warning.
 *
 * @returns Worker instance or null if unavailable
 */
function getWorker(): Worker | null {
  if (!workerAvailable) {
    return null;
  }

  if (workerInstance) {
    return workerInstance;
  }

  try {
    // Vite worker import - automatically handled by Vite's build system
    // The ?worker suffix tells Vite to bundle this as a Web Worker
    workerInstance = new Worker(
      new URL('../workers/layoutWorker.ts', import.meta.url),
      { type: 'module' }
    );

    console.log('[LayoutService] Web Worker initialized successfully');
    return workerInstance;
  } catch (error) {
    console.warn('[LayoutService] Web Worker initialization failed, will use main-thread fallback:', error);
    workerAvailable = false;
    return null;
  }
}

/**
 * Compute tree layout asynchronously using Web Worker (with fallback)
 *
 * This is the main entry point for layout computation. It attempts to use
 * a Web Worker for off-thread computation, but falls back to main-thread
 * computation if the worker is unavailable or fails.
 *
 * @param persons - All persons in the tree
 * @param relationships - All relationships (parent_child, spouse)
 * @param focalPersonId - Optional root person ID
 * @param couples - Couple metadata for union visualization
 * @param options - Layout options (depth limits, etc.)
 * @returns Promise<TreeLayout> - Complete tree layout
 */
export async function computeLayoutAsync(
  persons: PersonNodeCanvas[],
  relationships: RelationshipEdge[],
  focalPersonId?: string,
  couples: Couple[] = [],
  options?: LayoutOptions
): Promise<TreeLayout> {
  const worker = getWorker();

  // Fallback: Use main-thread computation if worker unavailable
  if (!worker) {
    console.log('[LayoutService] Using main-thread computation (worker unavailable)');
    const startTime = performance.now();

    const layout = computeTreeLayout(
      persons,
      relationships,
      focalPersonId,
      couples,
      options
    );

    const duration = Math.round(performance.now() - startTime);
    console.log('[LayoutService] Main-thread computation time:', `${duration}ms`);

    return layout;
  }

  // Worker available: Use off-thread computation
  console.log('[LayoutService] Using Web Worker for layout computation');

  return new Promise<TreeLayout>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      console.warn('[LayoutService] Worker computation timeout, falling back to main thread');

      // Timeout: Fall back to main-thread computation
      try {
        const layout = computeTreeLayout(
          persons,
          relationships,
          focalPersonId,
          couples,
          options
        );
        resolve(layout);
      } catch (error) {
        reject(error);
      }
    }, 10000); // 10-second timeout for very large trees

    const handleMessage = (event: MessageEvent<LayoutResultMessage | LayoutErrorMessage>) => {
      clearTimeout(timeoutId);
      worker.removeEventListener('message', handleMessage);
      worker.removeEventListener('error', handleError);

      const response = event.data;

      if (response.type === 'LAYOUT_RESULT') {
        resolve(response.payload);
      } else if (response.type === 'LAYOUT_ERROR') {
        console.error('[LayoutService] Worker computation failed:', response.error);

        // Worker error: Fall back to main-thread computation
        console.log('[LayoutService] Falling back to main-thread computation after worker error');
        try {
          const layout = computeTreeLayout(
            persons,
            relationships,
            focalPersonId,
            couples,
            options
          );
          resolve(layout);
        } catch (error) {
          reject(error);
        }
      }
    };

    const handleError = (error: ErrorEvent) => {
      clearTimeout(timeoutId);
      worker.removeEventListener('message', handleMessage);
      worker.removeEventListener('error', handleError);

      console.error('[LayoutService] Worker error:', error);

      // Worker error: Fall back to main-thread computation
      console.log('[LayoutService] Falling back to main-thread computation after worker error');
      try {
        const layout = computeTreeLayout(
          persons,
          relationships,
          focalPersonId,
          couples,
          options
        );
        resolve(layout);
      } catch (fallbackError) {
        reject(fallbackError);
      }
    };

    worker.addEventListener('message', handleMessage);
    worker.addEventListener('error', handleError);

    // Send computation request to worker
    const message: LayoutComputeMessage = {
      type: 'COMPUTE_LAYOUT',
      payload: {
        persons,
        relationships,
        focalPersonId,
        couples,
        options,
      },
    };

    worker.postMessage(message);
  });
}

/**
 * Terminate the worker instance
 * Useful for cleanup during component unmount or app shutdown
 */
export function terminateWorker(): void {
  if (workerInstance) {
    workerInstance.terminate();
    workerInstance = null;
    console.log('[LayoutService] Web Worker terminated');
  }
}
